import Modal from 'components/common/Modal';
import axios from 'helpers/axios';
import config from 'helpers/config';
import dateFormats from 'helpers/dateFormats';
import textConstants from 'helpers/textConstants';
import Inspection from 'models/InspectionModel';
import InspectionTaskStatus from 'models/types/InspectionTaskStatus';
import moment from 'moment';
import React from 'react';
import { CgFileDocument } from 'react-icons/cg';
import { CheckCircle, AlertTriangle, HelpCircle, Clock, User } from 'lucide-react';
import {
    FaCamera,
    FaCheck,
    FaChevronDown,
    FaChevronUp,
    FaExclamation,
    FaExclamationCircle,
    FaExclamationTriangle,
} from 'react-icons/fa';
import UserFullName from 'components/common/UserFullName';
import FullPhotoView, { PhotoFileModel } from 'components/common/FullPhotoView';

interface Props {
    inspectionId: string;
    onClose: () => void;
}

interface State {
    inspection?: Inspection;
    isLoading: boolean;
    isPhotoExpanded?: boolean;
    expandedPhoto?: any;
    expandedGroups: { [key: string]: boolean };
    photoFiles: PhotoFileModel[];
    initialPhotoIndex?: number;
}

class InspectionViewModal extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            isLoading: true,
            expandedGroups: {},
            isPhotoExpanded: false,
            expandedPhoto: undefined,
            photoFiles: [],
        };
    }

    toggleGroup = (groupId: string) => {
        this.setState((prevState) => ({
            expandedGroups: {
                ...prevState.expandedGroups,
                [groupId]: !prevState.expandedGroups[groupId],
            },
        }));
    };

    componentDidMount() {
        this.getInspection();
    }

    viewFullPhoto(photos: any[], index: number = 0) {
        this.setState({
            isPhotoExpanded: true,
            expandedPhoto: photos[0],
            photoFiles: photos.map((file) => ({
                id: file.id,
                fileName: file.fileName,
                photoSourceURL: `${config.fileRootURL}/images/${file.fileName}`,
            })),
            initialPhotoIndex: index,
        });
    }

    getInspection() {
        axios.get(`${config.apiUrl}/inspection/${this.props.inspectionId}`).then((res) => {
            const inspection = res.data;

            // Initialize all groups as expanded by default
            const expandedGroups: { [key: string]: boolean } = {};
            inspection.groups.forEach((group: any) => {
                expandedGroups[group.id] = true;
            });

            this.setState({ inspection, isLoading: false, expandedGroups });
        });
    }

    renderStatusCircle = (task: any) => {
        return (
            <div className="flex gap-2">
                <button
                    className={`w-6 h-6 rounded-full border-2 border-green-400 ${
                        task.rating === InspectionTaskStatus.OK
                            ? 'flex items-center justify-center text-white bg-green-500'
                            : ''
                    } border-green-500 mr-1`}
                >
                    {task.rating === InspectionTaskStatus.OK && <FaCheck className="text-white text-xs m-1" />}
                </button>

                <button
                    className={`w-6 h-6 rounded-full border-2 border-yellow-400 ${
                        task.rating === InspectionTaskStatus.NeedsAttention
                            ? 'flex items-center justify-center text-white bg-yellow-500'
                            : ''
                    } border-yellow-500 mr-1`}
                >
                    {task.rating === InspectionTaskStatus.NeedsAttention && (
                        <FaExclamationTriangle className="text-white text-xs m-1" />
                    )}
                </button>

                <button
                    className={`w-6 h-6 rounded-full border-2 border-red-400 ${
                        task.rating === InspectionTaskStatus.NeedsRepair
                            ? 'flex items-center justify-center text-white bg-red-500'
                            : ''
                    } border-red-500`}
                >
                    {task.rating === InspectionTaskStatus.NeedsRepair && (
                        <FaExclamation className="text-white text-xs m-1.5" />
                    )}
                </button>
            </div>
        );
    };

    render() {
        const { inspection, isLoading, expandedGroups, isPhotoExpanded, expandedPhoto, photoFiles, initialPhotoIndex } =
            this.state;

        return (
            <Modal
                headerText={textConstants.INSPECTION_DETAILS + ' - ' + inspection?.name}
                isOpen={true}
                onClose={() => this.props.onClose()}
                isBackgroundWhite={true}
                isDeleteHidden={true}
                saveText={'Print Report'}
                isSaveHidden={false}
                bodyClassName="no-padding"
            >
                <div>
                    {isPhotoExpanded && (
                        <FullPhotoView
                            photoFiles={photoFiles}
                            onClose={() =>
                                this.setState({
                                    isPhotoExpanded: false,
                                    expandedPhoto: null,
                                    photoFiles: [],
                                    initialPhotoIndex: undefined,
                                })
                            }
                            initialPhotoIndex={initialPhotoIndex}
                        />
                    )}
                    {!isLoading && inspection && (
                        <div>
                            <div className="">
                                <div className="px-4 py-4 bg-gray-50 p-4 border-b border-gray-200">
                                    <div className="flex items-center text-sm text-gray-600 mb-2">
                                        <User size={14} className="mr-1" />
                                        <span className="font-semibold mr-1">Inspector:</span>
                                        <span>
                                            <UserFullName userId={inspection?.createdById} />
                                        </span>
                                    </div>

                                    <div className="flex flex-wrap gap-4">
                                        <div className="flex items-center text-sm text-gray-600">
                                            <Clock size={14} className="mr-1" />
                                            <span className="font-semibold mr-1">Started:</span>
                                            <span>
                                                {moment(inspection?.createdAt).format(dateFormats.USA_DATETIME)}
                                            </span>
                                        </div>

                                        <div className="flex items-center text-sm text-gray-600">
                                            <CheckCircle size={14} className="mr-1" />
                                            <span className="font-semibold mr-1">Completed:</span>
                                            {inspection?.completionDate ? (
                                                <span>
                                                    {moment(inspection?.completionDate).format(
                                                        dateFormats.USA_DATETIME,
                                                    )}
                                                </span>
                                            ) : (
                                                <span className="text-blue-500 font-semibold">Still in Progress</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="p-4 border-b border-gray-200">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Inspection Summary</h3>
                                    <div className="grid grid-cols-4 gap-2">
                                        <div className="bg-green-50 border border-green-200 rounded-md p-3 flex flex-col items-center">
                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-700 font-semibold mb-1">
                                                {inspection.groups?.reduce((acc, group) => {
                                                    return (
                                                        acc +
                                                        group.tasks?.filter(
                                                            (task) => task.rating === InspectionTaskStatus.OK,
                                                        ).length
                                                    );
                                                }, 0)}
                                            </div>
                                            <div className="flex items-center text-green-700 text-sm font-semibold">
                                                <CheckCircle size={14} className="mr-1" />
                                                <span>Checked and OK</span>
                                            </div>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 flex flex-col items-center">
                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100 text-yellow-700 font-semibold mb-1">
                                                {inspection.groups?.reduce((acc, group) => {
                                                    return (
                                                        acc +
                                                        group.tasks?.filter(
                                                            (task) =>
                                                                task.rating === InspectionTaskStatus.NeedsAttention,
                                                        ).length
                                                    );
                                                }, 0)}
                                            </div>
                                            <div className="flex items-center text-yellow-700 text-sm font-semibold">
                                                <AlertTriangle size={14} className="mr-1" />
                                                <span>May need attention</span>
                                            </div>
                                        </div>

                                        <div className="bg-red-50 border border-red-200 rounded-md p-3 flex flex-col items-center">
                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-700 font-semibold mb-1">
                                                {inspection.groups?.reduce((acc, group) => {
                                                    return (
                                                        acc +
                                                        group.tasks?.filter(
                                                            (task) => task.rating === InspectionTaskStatus.NeedsRepair,
                                                        ).length
                                                    );
                                                }, 0)}
                                            </div>
                                            <div className="flex items-center text-red-700 text-sm font-semibold">
                                                <FaExclamationCircle className="text-red-700 mr-1 " size={24} />

                                                <span>Needs immediate attention</span>
                                            </div>
                                        </div>

                                        <div className="bg-gray-50 border border-gray-200 rounded-md p-3 flex flex-col items-center">
                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-700 font-semibold mb-1">
                                                {inspection.groups?.reduce((acc, group) => {
                                                    return acc + group.tasks?.filter((task) => !!!task.rating).length;
                                                }, 0)}
                                            </div>
                                            <div className="flex items-center text-gray-700 text-sm font-semibold">
                                                <HelpCircle size={14} className="mr-1" />
                                                <span>Not Inspected</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                {inspection.groups.map((group, groupIndex) => {
                                    const isExpanded = expandedGroups[group.id];

                                    return (
                                        <div key={`group-${group.id}`} className="border-y">
                                            <div
                                                className="flex justify-between items-center p-2 bg-blue-50 hover:bg-blue-100 cursor-pointer px-4"
                                                onClick={() => this.toggleGroup(group.id)}
                                            >
                                                <h3 className="font-semibold text-sm text-blue-900">{group.name}</h3>
                                                {isExpanded ? (
                                                    <FaChevronUp className="text-blue-700" />
                                                ) : (
                                                    <FaChevronDown className="text-blue-700" />
                                                )}
                                            </div>

                                            {isExpanded && (
                                                <div className="px-4">
                                                    {group.tasks.map((task, taskIndex) => (
                                                        <div
                                                            key={`task-${group.id}-${taskIndex}`}
                                                            className="border-b py-2"
                                                        >
                                                            <div className="flex justify-between items-center mb-1">
                                                                <div className="font-semibold">
                                                                    #{taskIndex + 1} {task.description}
                                                                </div>
                                                                {this.renderStatusCircle(task)}
                                                            </div>

                                                            {task.finding && (
                                                                <div
                                                                    className={`
                                                                    p-2 mb-2 rounded
                                                                    ${
                                                                        task.rating === InspectionTaskStatus.NeedsRepair
                                                                            ? 'bg-red-50 border-l-4 border-red-500'
                                                                            : task.rating ===
                                                                              InspectionTaskStatus.NeedsAttention
                                                                            ? 'bg-yellow-50 border-l-4 border-yellow-500'
                                                                            : 'bg-gray-50'
                                                                    }
                                                                `}
                                                                >
                                                                    {task.finding}
                                                                </div>
                                                            )}

                                                            {!task.finding &&
                                                                task.rating === InspectionTaskStatus.OK && (
                                                                    <div className="text-sm text-gray-600 mb-2">
                                                                        No issues found
                                                                    </div>
                                                                )}

                                                            {task.files && task.files.length > 0 && (
                                                                <div>
                                                                    <div className="flex items-center text-xs text-gray-500 mb-1">
                                                                        <FaCamera className="mr-1" />
                                                                        <span>Photo evidence</span>
                                                                        <span className="ml-auto">
                                                                            {task.files[0]?.createdAt &&
                                                                                moment(task.files[0].createdAt).format(
                                                                                    dateFormats.USA_DATETIME,
                                                                                )}
                                                                        </span>
                                                                    </div>

                                                                    <div className="flex flex-wrap">
                                                                        {task.files
                                                                            .filter((file) => file.type === 'image')
                                                                            .sort((a, b) =>
                                                                                new Date(a.createdAt) >
                                                                                new Date(b.createdAt)
                                                                                    ? 1
                                                                                    : -1,
                                                                            )
                                                                            .map((file, photoIndex) => (
                                                                                <div
                                                                                    key={file.id}
                                                                                    className="mr-2 mb-2"
                                                                                >
                                                                                    <img
                                                                                        src={`${config.fileRootURL}/images/${file.fileName}`}
                                                                                        alt={file.fileName}
                                                                                        className="border rounded cursor-pointer"
                                                                                        style={{
                                                                                            maxWidth: '240px',
                                                                                            maxHeight: '180px',
                                                                                        }}
                                                                                        onClick={() =>
                                                                                            this.viewFullPhoto(
                                                                                                task.files
                                                                                                    .filter(
                                                                                                        (f) =>
                                                                                                            f.type ===
                                                                                                            'image',
                                                                                                    )
                                                                                                    .sort((a, b) =>
                                                                                                        new Date(
                                                                                                            a.createdAt,
                                                                                                        ) >
                                                                                                        new Date(
                                                                                                            b.createdAt,
                                                                                                        )
                                                                                                            ? 1
                                                                                                            : -1,
                                                                                                    ),
                                                                                                photoIndex,
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            ))}
                                                                    </div>

                                                                    <div>
                                                                        {task.files
                                                                            .filter((file) => file.type === 'document')
                                                                            .sort((a, b) =>
                                                                                new Date(a.createdAt) >
                                                                                new Date(b.createdAt)
                                                                                    ? 1
                                                                                    : -1,
                                                                            )
                                                                            .map((file) => (
                                                                                <div
                                                                                    key={file.id}
                                                                                    className="flex items-center mb-1"
                                                                                >
                                                                                    <CgFileDocument className="mr-1 text-blue-600" />
                                                                                    <a
                                                                                        className="text-blue-600 text-sm hover:underline"
                                                                                        href={`${config.fileRootURL}/documents/${file.fileName}`}
                                                                                        download={`${file.fileName}`}
                                                                                        rel="noreferrer"
                                                                                        title={file.fileName}
                                                                                        target="_blank"
                                                                                    >
                                                                                        {file.fileName}
                                                                                    </a>
                                                                                </div>
                                                                            ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
        );
    }
}

export default InspectionViewModal;
